import {
    addMonths,
    differenceInCalendarDays,
    differenceInCalendarMonths,
    eachMonthOfInterval,
    format,
    isAfter,
    startOfMonth,
} from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { getLinePins } from "./equipment-demand"

/**
 * Helpers around planItem.monthly - amounts typed month by month on a recurring cost line
 * ({ month: "yyyy-MM", cost, bill }). The money rules live in mf-utils.js#getMonthlyAmounts
 * (and the API engine); these just move months around.
 *
 * Months outside the line's dates stay stored (a line that grows back gets them back) but
 * don't exist for the user: they don't count, aren't shown and aren't copied.
 */

const MONTH_KEY = /^\d{4}-\d{2}$/

export const monthKey = (date) => format(date, "yyyy-MM")

/** "2026-06" -> local Date on June 1st */
export const monthKeyToDate = (key) => {
    const [y, m] = key.split("-").map(Number)
    return new Date(y, m - 1, 1)
}

/** The months a line covers, from its start month to its end month: ["2026-06", ...] */
export function getLineMonths(planItem) {
    if (!planItem?.startDate || !planItem?.endDate) return []

    const start = startOfMonth(toZonedTime(planItem.startDate))
    const end = toZonedTime(planItem.endDate)
    if (isAfter(start, end)) return []

    return eachMonthOfInterval({ start, end }).map(monthKey)
}

/** The typed months inside the line's dates - the only ones there are, to the user */
export function getMonthlyInLine(planItem) {
    const months = new Set(getLineMonths(planItem))

    return (planItem?.monthly || []).filter((m) => months.has(m?.month))
}

// A month as a role's monthly schedule counts one when it follows a move (api
// planItemSchedule.shift.js#getRoleChange)
const AVERAGE_MONTH_DAYS = 365.25 / 12

/**
 * planItem.monthly after its dates change, or undefined when it stays as it is.
 *
 * A line that moves - both of its dates go the same way - takes its typed months along, by
 * how far its start moved (its end, when only the end is pinned) in whole months, rounded:
 * a few days across a month end leaves them where they are, a month or more takes them that
 * many months on. A line that only gets longer or shorter keeps them on their calendar
 * months, and the ones it no longer covers stop counting. API twin:
 * missionPlan.taskDates.js#getMovedMonthly.
 *
 * @param planItem {object} The plan item before the change
 * @param change {object} What is being saved: { startDate, endDate, ... } - a date it
 *   leaves out stays as it is
 * @return {object[]|undefined}
 */
export function getMovedMonthly(planItem, change) {
    if (!planItem?.monthly?.length || !change || change.monthly !== undefined) return undefined

    const startDate = change.startDate ?? planItem.startDate
    const endDate = change.endDate ?? planItem.endDate

    if (!planItem.startDate || !planItem.endDate || !startDate || !endDate) return undefined

    const was = { start: toZonedTime(planItem.startDate), end: toZonedTime(planItem.endDate) }
    const now = { start: toZonedTime(startDate), end: toZonedTime(endDate) }
    const startDays = differenceInCalendarDays(now.start, was.start)
    const endDays = differenceInCalendarDays(now.end, was.end)

    if (!startDays || Math.sign(startDays) !== Math.sign(endDays)) return undefined

    const pins = getLinePins(planItem)
    const by = Math.round((pins.end && !pins.start ? endDays : startDays) / AVERAGE_MONTH_DAYS)

    if (!by) return undefined

    return planItem.monthly
        .filter((m) => MONTH_KEY.test(m?.month || ""))
        .map((m) => ({ ...m, month: monthKey(addMonths(monthKeyToDate(m.month), by)) }))
}

/**
 * planItem.monthly with the revenue typed on it cleared (fixed bid bills milestones only),
 * or undefined when no month has any. Typed costs stay.
 */
export function clearTypedBill(planItem) {
    const monthly = planItem?.monthly || []
    const typed = (v) => v !== undefined && v !== null && v !== ""

    if (!monthly.some((m) => typed(m?.bill))) return undefined

    return monthly.filter((m) => typed(m?.cost)).map((m) => ({ month: m.month, cost: m.cost }))
}

/**
 * Typed months as offsets from the line's first month, for templates (whose dates move):
 * [{ offset: 0, cost: 2000 }, ...]
 */
export function toMonthlyOffsets(planItem) {
    const monthly = getMonthlyInLine(planItem)

    if (!monthly.length || !planItem.startDate) return undefined

    const first = startOfMonth(toZonedTime(planItem.startDate))

    return monthly
        .map((m) => ({
            offset: differenceInCalendarMonths(monthKeyToDate(m.month), first),
            ...(m.cost !== undefined ? { cost: m.cost } : {}),
            ...(m.bill !== undefined ? { bill: m.bill } : {}),
        }))
}

/** Back from offsets onto the calendar, counting from the new line's start date (local) */
export function fromMonthlyOffsets(offsets, startDate) {
    if (!offsets?.length || !startDate) return undefined

    const first = startOfMonth(startDate)

    return offsets
        .filter((o) => Number.isInteger(o?.offset))
        .map((o) => ({
            month: monthKey(addMonths(first, o.offset)),
            ...(o.cost !== undefined ? { cost: o.cost } : {}),
            ...(o.bill !== undefined ? { bill: o.bill } : {}),
        }))
}
